import {
  Alert,
  Button,
  Center,
  Container,
  Group,
  Input,
  Text,
  Title,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDisclosure, useScrollIntoView } from "@mantine/hooks";
import { IconInfoCircle, IconRefresh } from "@tabler/icons-react";
import { IncomeDetails, IncomeTaxBreakdown } from "backend/src/types";
import React, { FormEvent } from "react";
import {
  IncomeFormValues,
  incomeFormActions,
  useIncomeFormContext,
} from "../../context/form-context";
import { getSlug, getTitle } from "../../utils";
import ConfigureSalaryModal from "../ConfigureSalaryModal/ConfigureSalaryModal";
import { InputWithButton } from "../InputWithButton/InputWithButton";
import { StatsGrid } from "../StatsGrid/StatsGrid";
import { TableSalaries } from "../TableSalaries/TableSalaries";
import ShareDeepLinkButton from "../ShareDeepLinkButton/ShareDeepLinkButton";

type IncomeTaxCalculatorFormProps = {
  incomeForm: UseFormReturnType<
    IncomeFormValues,
    (values: IncomeFormValues) => IncomeFormValues
  >;
};

const IncomeTaxCalculatorForm: React.FC<IncomeTaxCalculatorFormProps> = ({
  incomeForm,
}) => {
  const [data, setData] = React.useState<IncomeTaxBreakdown[]>();
  const [opened, { open, close }] = useDisclosure(false);
  const [closeDisclaimer, setCloseDisclaimer] = React.useState<boolean>(false);
  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    offset: 60,
  });

  const openSalary1Modal = () => {
    incomeFormActions.setFieldValue("currentPath", "salary1");
    open();
  };

  const openSalary2Modal = () => {
    incomeFormActions.setFieldValue("currentPath", "salary2");
    open();
  };

  const onCloseSalaryModel = () => {
    close();
    scrollIntoView({
      alignment: "center",
    });
  };

  const form = useIncomeFormContext();

  const handleIncomeFormSubmit = incomeForm.onSubmit((values) => {
    console.info("<><><> submit values", values);
  });

  const handleIncomeFormReset = (event: FormEvent<HTMLFormElement>) => {
    incomeForm.onReset(event);
    incomeForm.setFieldValue("salary1.grossIncome", "");
    setData(undefined);
  };

  React.useEffect(() => {
    let salaries: Partial<IncomeDetails>[] = [];
    const values = form.getTransformedValues();
    if (form.values.salary1.grossIncome) {
      salaries.push({
        grossIncome: +form.values.salary1.grossIncome,
        pensionType: values.salary1.pensionType,
        pensionContributionsType: values.salary1.pensionContributionsType,
        pensionContributions: values.salary1.pensionContributions,
        studentLoanPlans: values.salary1.studentLoanPlans,
        giftAid: values.salary1.giftAid,
        isBlind: values.salary1.isBlind,
        niTaxCategory: values.salary1.niTaxCategory,
        taxYear: values.salary1.taxYear,
        ...(values.salary1.taxCode ? { taxCode: values.salary1.taxCode } : {}),
      });
      if (form.values.salary2.grossIncome) {
        salaries.push({
          grossIncome: +form.values.salary2.grossIncome,
          pensionType: values.salary2.pensionType,
          pensionContributionsType: values.salary2.pensionContributionsType,
          pensionContributions: values.salary2.pensionContributions,
          studentLoanPlans: values.salary2.studentLoanPlans,
          giftAid: values.salary2.giftAid,
          isBlind: values.salary2.isBlind,
          niTaxCategory: values.salary2.niTaxCategory,
          taxYear: values.salary2.taxYear,
          ...(values.salary2.taxCode
            ? { taxCode: values.salary2.taxCode }
            : {}),
        });
      }
      fetch(
        `${process.env.REACT_APP_INCOME_TAX_CALCULATOR_API_URL}/compare-salaries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            salaries,
          }),
        }
      )
        .then((res) => res.json())
        .then((data) => setData(data));
    }
  }, [form.values.salary1, form.values.salary2]);

  const renderSlug = React.useMemo(() => {
    if (!data?.length) {
      return null;
    }
    let deepLink = `${
      process.env.REACT_APP_INCOME_TAX_CALCULATOR_HOST_URL
    }/calculator/${getSlug(data[0].inputs)}`;
    let shareWord = "salary";
    if (data?.length === 2) {
      deepLink += `-vs-${getSlug(data[1].inputs)}`;
      shareWord = "comparison";
    }
    return (
      <Center>
        <Group mt="xl" mb="xl">
          <Text fz="sm">Share this {shareWord}</Text>
          <Input readOnly type="text" value={deepLink} />
          <ShareDeepLinkButton url={deepLink} />
        </Group>
      </Center>
    );
  }, [data]);

  const renderStats = React.useMemo(() => {
    if (typeof data !== "undefined" && data.length && data.length > 1) {
      return (
        <>
          <Container w="80%" mt="xl">
            <Title order={2}>Salary Comparison</Title>
            <Text fz="sm">
              {getTitle(data[0].inputs)} vs. {getTitle(data[1].inputs)}
            </Text>
          </Container>
          <StatsGrid data={data} />
        </>
      );
    }
    return null;
  }, [data]);

  const hasData = typeof data !== "undefined" && data.length > 0;

  const renderTable = React.useMemo(() => {
    if (hasData) {
      return <TableSalaries data={data} />;
    }
    return null;
  }, [hasData, data]);

  const renderResetButton = React.useMemo(() => {
    if (hasData) {
      return (
        <Center>
          <Button
            type="reset"
            radius="xl"
            variant="outline"
            leftSection={<IconRefresh />}
          >
            Start over
          </Button>
        </Center>
      );
    }
    return null;
  }, [hasData]);

  const onCloseDisclaimer = () => setCloseDisclaimer(true);

  return (
    <form onSubmit={handleIncomeFormSubmit} onReset={handleIncomeFormReset}>
      <Group w="100%" justify="center" mb={!hasData ? 300 : "xl"}>
        <Group mt="xl" mb="xl" hiddenFrom="sm">
          <InputWithButton
            pathPrefix="salary1"
            value={form.values.salary1.grossIncome}
            onChange={(value) => {
              form.setFieldValue("salary1.grossIncome", +value);
            }}
            label={hasData && "Salary 1"}
            onRightSectionClick={openSalary1Modal}
            w="95%"
          />
          {hasData && (
            <InputWithButton
              pathPrefix="salary2"
              value={form.values.salary2.grossIncome}
              onChange={(value) => {
                form.setFieldValue("salary2.grossIncome", +value);
              }}
              label="Salary 2"
              onRightSectionClick={openSalary2Modal}
              w="95%"
            />
          )}
        </Group>
        <Group
          mt="xl"
          mb="xl"
          grow
          visibleFrom="sm"
          w={!hasData ? "50%" : "90%"}
        >
          <InputWithButton
            pathPrefix="salary1"
            value={form.values.salary1.grossIncome}
            onChange={(value) => {
              form.setFieldValue("salary1.grossIncome", +value);
            }}
            label={hasData && "Salary 1"}
            onRightSectionClick={openSalary1Modal}
          />
          {hasData && (
            <InputWithButton
              pathPrefix="salary2"
              value={form.values.salary2.grossIncome}
              onChange={(value) => {
                form.setFieldValue("salary2.grossIncome", +value);
              }}
              label="Salary 2"
              onRightSectionClick={openSalary2Modal}
            />
          )}
        </Group>
      </Group>

      <Group mt="xl" mb="xl">
        {!closeDisclaimer && hasData && (
          <Container size="sm">
            <Alert
              withCloseButton
              variant="light"
              color="red"
              title="Disclaimer"
              icon={<IconInfoCircle />}
              onClose={onCloseDisclaimer}
            >
              The information provided by this tool is for illustrative purposes
              only. Your actual payslip may vary depending on multiple factors
              not accounted for by this tool. Always seek professional advice
              from a qualified financial advisor before making major financial
              decisions.
            </Alert>
          </Container>
        )}
      </Group>
      <div ref={targetRef}>{renderTable}</div>
      {renderSlug}
      {renderResetButton}
      {renderStats}
      <ConfigureSalaryModal opened={opened} onClose={onCloseSalaryModel} />
    </form>
  );
};

export default IncomeTaxCalculatorForm;
